import { memo, useMemo } from 'react';
import { Navigate, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { PermissionDeniedProvider } from '../../contexts/PermissionDeniedContext';
import { PotreeSourcesContext } from '../../contexts/PotreeSourcesContext';
import { UserContext, UserContextType } from '../../contexts/UserContext';
import { usePotreeSources } from '../../hooks/usePotreeSources';
import { useDarkModeTheme } from '../../modules/Properties/integration/UserSettingsIntegration';
import { sharedProjectPath } from '../../share/shareMode';
import { T } from '../../translation/src';
import { DateFormats, Language, MeasurementUnits, SharedProjectInfoQuery, UserRole, useCurrentUserQuery, useSharedProjectInfoQuery } from '../../types/graphqlTypes';

type SharedProjectInfo = NonNullable<SharedProjectInfoQuery['sharedProjectInfo']>;

/**
 * The viewer and everything under it reads the current user from UserContext. A share-link
 * visitor without a session gets a stand-in guest of the project's organisation (the page is
 * read-only regardless; Project.tsx also forces it from the /share URL).
 */
const createGuestUser = (organisationId: string): UserContextType => ({
  id: '',
  name: 'Guest',
  email: '',
  firstName: 'Guest',
  lastName: '',
  isSuperAdmin: false,
  rolesByOrganisation: [{ organisationId, role: UserRole.Guest }],
  organisations: [],
  favoriteProjectIds: [],
  measurementUnit: MeasurementUnits.Meters,
  dateFormat: DateFormats.Ddmmyyyy,
  language: Language.En,
  properties: [],
});

const ShareLinkInvalid: React.FC2 = () => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center dark:bg-[#313131] dark:text-white flex-col w-full h-full">
      <div className="mb-3 text-lg font-bold">
        <T _str="This link is no longer valid." />
      </div>
      <div className="mb-6">
        <T _str="Sharing was turned off or the link was reset. Ask the project owner for a new one." />
      </div>
      <Button variant="primary" onClick={() => navigate('/login')}>
        <T _str="log in" swc />
      </Button>
    </div>
  );
};

/**
 * Shell for /share/:token. Replaces the login-walled <App/>: no navigation, one project, read-only.
 * The token itself is sent by the Apollo link (see share/shareMode.ts); here we only find out
 * which project it unlocks and provide the contexts the viewer expects. A visitor who is logged
 * in keeps their account; one who already belongs to the project's organisation is sent to the
 * regular project page, where they have their full rights.
 */
const SharedProjectApp_: React.FC2 = () => {
  const potreeSourcesLoaded = usePotreeSources();
  const { data, loading } = useSharedProjectInfoQuery({ fetchPolicy: 'network-only' });
  const { data: userData, loading: userLoading } = useCurrentUserQuery();
  const info = data?.sharedProjectInfo ?? null;
  const currentUser = userData?.currentUser ?? null;
  const guestUser = useMemo(() => createGuestUser(info?.organisationId ?? ''), [info?.organisationId]);
  useDarkModeTheme();

  if (loading || userLoading) {
    return (
      <div className="flex items-center-justify-center">
        <EarthLoader />
      </div>
    );
  }
  if (!info) return <ShareLinkInvalid />;

  const isMember = !!currentUser && (currentUser.isSuperAdmin || currentUser.rolesByOrganisation.some((role) => role.organisationId === info.organisationId));
  if (isMember) return <Navigate replace to={`/organisations/${info.organisationId}/projects/${info.projectId}`} />;

  return (
    <UserContext.Provider value={currentUser ?? guestUser}>
      <PotreeSourcesContext.Provider value={{ isLoaded: potreeSourcesLoaded }}>
        <PermissionDeniedProvider>
          <div className="flex w-full h-full overflow-auto">
            <div className="flex flex-grow overflow-auto">
              <Outlet context={info} />
            </div>
          </div>
        </PermissionDeniedProvider>
      </PotreeSourcesContext.Provider>
    </UserContext.Provider>
  );
};

export const SharedProjectApp = memo(SharedProjectApp_);

/** Index route of /share/:token: forwards to the project URL shape the viewer's useParams() consumers expect. */
const SharedProjectRedirect_: React.FC2 = () => {
  const info = useOutletContext<SharedProjectInfo>();
  const { token = '' } = useParams();
  return <Navigate replace to={sharedProjectPath({ token, organisationId: info.organisationId, projectId: info.projectId })} />;
};

export const SharedProjectRedirect = memo(SharedProjectRedirect_);
